import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';

import PropTypes from 'prop-types';

import { t, number } from '~/helpers';

function Grafico({
  data,
  intervalosJaArqueados,
  intervalosCapturados,
  intervaloAceito,
}) {
  const categoriasAltura = useMemo(
    () => data.map((item) => item.alturaarqueacaoitem),
    [data]
  );

  const estaNoIntervalo = (altura, intervalos = []) =>
    intervalos.some(
      ({ alturainicial, alturafinal }) =>
        Number(altura) >= Number(alturainicial) &&
        Number(altura) <= Number(alturafinal)
    );

  const yMax = useMemo(() => {
    const maiorVolume = Math.max(
      ...data.map((item) =>
        Math.max(
          Number(item.volumearqueacaoitem || 0),
          Number(item.volumeoriginalarqueacaoitemautomatica || 0)
        )
      )
    );

    return maiorVolume * 1.05;
  }, [data]);

  const {
    volumeAjustadoNormal,
    volumeAtual,
    areaIntervaloAceito,
    areaIntervalosCapturados,
    areaIntervalosJaArqueados,
  } = useMemo(() => {
    const ajustadoNormal = [];
    const atual = [];
    const aceito = [];
    const utilizados = [];
    const jaArqueados = [];

    data.forEach((item) => {
      const altura = item.alturaarqueacaoitem;

      ajustadoNormal.push(item.volumearqueacaoitem);
      atual.push(item.volumeoriginalarqueacaoitemautomatica);

      const estaAceito = intervaloAceito
        ? Number(altura) >= Number(intervaloAceito.alturainicial) &&
          Number(altura) <= Number(intervaloAceito.alturafinal)
        : false;

      aceito.push(estaAceito ? yMax : null);
      utilizados.push(
        estaNoIntervalo(altura, intervalosCapturados) ? yMax : null
      );
      jaArqueados.push(
        estaNoIntervalo(altura, intervalosJaArqueados) ? yMax : null
      );
    });

    return {
      volumeAjustadoNormal: ajustadoNormal,
      volumeAtual: atual,
      areaIntervaloAceito: aceito,
      areaIntervalosCapturados: utilizados,
      areaIntervalosJaArqueados: jaArqueados,
    };
  }, [
    data,
    intervalosCapturados,
    intervalosJaArqueados,
    intervaloAceito,
    yMax,
  ]);

  const series = [
    {
      name: 'Confirmado',
      data: areaIntervaloAceito,
      type: 'area',
    },
    {
      name: 'Capturado',
      data: areaIntervalosCapturados,
      type: 'area',
    },
    {
      name: 'Já Arqueado',
      data: areaIntervalosJaArqueados,
      type: 'area',
    },
    {
      name: 'Tabela Nova',
      data: volumeAjustadoNormal,
      type: 'line',
    },
    {
      name: 'Tabela Atual',
      data: volumeAtual,
      type: 'line',
    },
  ];

  const colors = ['#1b5e20', '#2e7d32', '#ff0000', '#4791db', '#f28c28'];

  return (
    <div style={{ height: '600px', width: '100%' }}>
      <Chart
        type="line"
        series={series}
        width="100%"
        height="100%"
        options={{
          colors,
          chart: {
            id: 'comparacao-volume-altura',
            stacked: false,
            toolbar: {
              show: false,
            },
            zoom: {
              enabled: true,
              type: 'xy',
              autoScaleYaxis: true,
            },
          },
          stroke: {
            curve: 'straight',
            width: [0, 0, 0, 2, 2],
          },
          fill: {
            opacity: [0.22, 0.14, 0.14, 1, 1],
          },
          dataLabels: {
            enabled: false,
          },
          xaxis: {
            type: 'numeric',
            categories: categoriasAltura,
            axisTicks: {
              show: false,
            },
            labels: {
              formatter(val) {
                return `${val} cm`;
              },
            },
          },
          yaxis: {
            min: 0,
            max: yMax,
            labels: {
              formatter(val) {
                return `${number(val)} L`;
              },
            },
          },
          markers: {
            size: [0, 0, 0, 0, 0],
          },
          tooltip: {
            shared: true,
            intersect: false,
            enabledOnSeries: [3, 4],
            marker: {
              show: false,
            },
            custom({ dataPointIndex }) {
              const categoriaAltura = data[dataPointIndex]?.alturaarqueacaoitem;
              const volAjustado = volumeAjustadoNormal[dataPointIndex];
              const volAtualPonto = volumeAtual[dataPointIndex];

              const diferenca =
                volAtualPonto != null && volAjustado != null
                  ? volAtualPonto - volAjustado
                  : null;

              return `
                <div class="apexcharts-tooltip-box" style="padding: 8px 10px;">
                  <div class="apexcharts-tooltip-x-group" style="margin-bottom: 6px;">
                    <span class="apexcharts-tooltip-text-x-label">Altura: </span>
                    <span class="apexcharts-tooltip-text-x-value">${
                      categoriaAltura ?? '-'
                    } cm</span>
                  </div>

                  <div class="apexcharts-tooltip-y-group" style="margin: 2px 0;">
                    <span
                      style="
                        background-color: ${colors[3]};
                        display: inline-block;
                        width: 10px;
                        height: 10px;
                        margin-right: 6px;
                        border-radius: 50%;
                      "
                    ></span>
                    <span>${t(
                      's.operacao.arqueacaoautomatica.volajustado'
                    )}: </span>
                    <span>${number(volAjustado, 2)} L</span>
                  </div>

                  <div class="apexcharts-tooltip-y-group" style="margin: 2px 0;">
                    <span
                      style="
                        background-color: ${colors[4]};
                        display: inline-block;
                        width: 10px;
                        height: 10px;
                        margin-right: 6px;
                        border-radius: 50%;
                      "
                    ></span>
                    <span>${t('s.operacao.recebimentonf.volatual')}: </span>
                    <span>${number(volAtualPonto, 2)} L</span>
                  </div>

                  ${
                    diferenca != null
                      ? `
                        <div style="border-top: 1px solid #eee; margin-top: 6px; padding-top: 6px;">
                          <span>${t(
                            's.relatorio.alteracaoencerrante.diferenca'
                          )}: </span>
                          <span>${number(diferenca, 2)} L</span>
                        </div>
                      `
                      : ''
                  }
                </div>
              `;
            },
          },
          legend: {
            show: true,
            position: 'top',
            horizontalAlign: 'right',
            floating: false,
            onItemClick: {
              toggleDataSeries: true,
            },
            onItemHover: {
              highlightDataSeries: true,
            },
          },
        }}
      />
    </div>
  );
}

Grafico.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      alturaarqueacaoitem: PropTypes.number.isRequired,
      volumearqueacaoitem: PropTypes.number.isRequired,
      volumeoriginalarqueacaoitemautomatica: PropTypes.number.isRequired,
    })
  ).isRequired,
  intervalosJaArqueados: PropTypes.arrayOf(
    PropTypes.shape({
      alturainicial: PropTypes.number.isRequired,
      alturafinal: PropTypes.number.isRequired,
    })
  ).isRequired,
  intervaloAceito: PropTypes.shape({
    alturainicial: PropTypes.number.isRequired,
    alturafinal: PropTypes.number.isRequired,
  }),
  intervalosCapturados: PropTypes.arrayOf(
    PropTypes.shape({
      alturainicial: PropTypes.number.isRequired,
      alturafinal: PropTypes.number.isRequired,
    })
  ).isRequired,
};

Grafico.defaultProps = {
  intervaloAceito: null,
};

export default Grafico;
